import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  getFeature, updateFeature, updateFeatureStatus, deleteFeature,
  FeatureStatus, Feature,
  getSummaryConfigs, summariesForFeature, SummaryConfig
} from '../api/client'
import { useNotify } from '../components/notice/useNotify'
import Spinner from '../components/Spinner'
import FeatureForm from '../components/FeatureForm'
import FeatureChartCard from '../charts/FeatureChartCard'
import FeatureSummaryCard from '../components/summaries/FeatureSummaryCard'
import AddSummaryButton from '../components/summaries/AddSummaryButton'
import ConfirmationModal from '../components/ConfirmationModal'
import { useCollections } from '../hooks/useCollections'
import { boolLabels } from '../utils/boolFeature'

const FeatureDetailPage: React.FC = () => {
  const notify = useNotify()
  const { featureId } = useParams<{ featureId: string }>()
  const navigate = useNavigate()
  
  const [feature, setFeature] = useState<Feature | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [loading, setLoading] = useState(true)
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [summaryConfigs, setSummaryConfigs] = useState<SummaryConfig[]>([])
  const { collections } = useCollections()
  
  const [editedFeature, setEditedFeature] = useState<{
    title: string,
    description: string,
    unit: string,
    start_date: string,
    end_date: string,
    config: Record<string, any>
  }>({
    title: '',
    description: '',
    unit: '',
    start_date: '',
    end_date: '',
    config: { type: 'freeform' } // Default to freeform type
  })
  
  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusMenuOpen) {
        const target = event.target as Node;
        const dropdown = document.querySelector('.status-dropdown-container');
        if (dropdown && !dropdown.contains(target)) {
          setStatusMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusMenuOpen]);

  // Load feature and checkins on component mount
  useEffect(() => {
    if (!featureId) return
    
    const fetchData = async () => {
      setLoading(true)
      try {
        const fetchedFeature = await getFeature(parseInt(featureId))
        
        setFeature(fetchedFeature)
        
        // Initialize edited feature with current values
        setEditedFeature({
          title: fetchedFeature.title,
          description: fetchedFeature.description || '',
          unit: fetchedFeature.unit || '',
          start_date: fetchedFeature.start_date ? fetchedFeature.start_date.split('T')[0] : '',
          end_date: fetchedFeature.end_date ? fetchedFeature.end_date.split('T')[0] : '',
          config: fetchedFeature.config || {}
        })
      } catch (err) {
        notify.error('Failed to load feature details')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    
    fetchData()
  }, [featureId])

  // Load the user's summary configs so we can render any that are pinned to
  // this feature's page above the chart. Failure is non-fatal — the card
  // section simply doesn't render. Exposed as a callback so the per-card
  // settings gear and "+ Add summary" header can trigger a re-fetch after
  // create / edit / delete.
  const reloadSummaryConfigs = React.useCallback(() => {
    getSummaryConfigs()
      .then((configs) => setSummaryConfigs(configs))
      .catch((err) => {
        console.error('Failed to load summary configs', err)
        setSummaryConfigs([])
      })
  }, [])

  useEffect(() => {
    if (!featureId) return
    reloadSummaryConfigs()
  }, [featureId, reloadSummaryConfigs])

  const handleStatusChange = async (newStatus: FeatureStatus) => {
    if (!featureId || !feature) return
    
    try {
      const updatedFeature = await updateFeatureStatus(parseInt(featureId), newStatus)
      setFeature(updatedFeature)
      notify.success('Feature status updated successfully')
    } catch (err) {
      notify.error('Failed to update feature status')
      console.error(err)
    }
  }
  
  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!featureId) return
    
    try {
      // Format the feature data properly before sending to API
      const featureToUpdate = {
        ...editedFeature,
        // Convert empty strings to undefined
        unit: editedFeature.unit ? editedFeature.unit : undefined,
        start_date: editedFeature.start_date ? editedFeature.start_date : undefined,
        end_date: editedFeature.end_date ? editedFeature.end_date : undefined
      }
      
      const updatedFeature = await updateFeature(parseInt(featureId), featureToUpdate)
      setFeature(updatedFeature)
      setEditMode(false)
      notify.success('Feature updated successfully')
    } catch (err) {
      notify.error('Failed to update feature: ' + (err.response?.data?.detail || err.message || 'Unknown error'))
      console.error(err)
    }
  }
  
  const handleDeleteFeature = async () => {
    if (!featureId) return

    try {
      await deleteFeature(parseInt(featureId))
      // Return to the owning project's page when we know it; otherwise fall
      // back to the home page.
      const dest = feature?.project_id ? `/projects/${feature.project_id}` : '/'
      navigate(dest)
    } catch (err) {
      notify.error('Failed to delete feature')
      console.error(err)
    }
    setShowDeleteConfirm(false)
  }
  
  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }
  
  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-blush-light">
        <div className="container mx-auto px-4 pb-8 sm:pt-8 flex justify-center items-center flex-grow">
          <Spinner />
        </div>
      </div>
    )
  }
  
  if (!feature) {
    return (
      <div className="flex flex-col min-h-screen bg-blush-light">
        <div className="container mx-auto px-4 pb-8 sm:pt-8">
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
            <h3 className="text-xl text-gray-700 mb-2">Feature not found</h3>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="flex flex-col min-h-screen bg-blush-light">
      
      <div className="container mx-auto px-4 pb-8 sm:pt-8">
        
        <div className="bg-white p-4 rounded-lg shadow-md mb-4">
          {editMode ? (
            <FeatureForm
              feature={editedFeature}
              setFeature={setEditedFeature}
              onSubmit={handleSaveEdit}
              isEditing={true}
              onCancel={() => setEditMode(false)}
              submitButtonText="Save"
            />
          ) : (
            <>
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                {/* Left side - Feature info */}
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-2">
                    <h1 className="text-2xl font-bold text-gray-800">{feature.title}</h1>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      feature.status === FeatureStatus.ACTIVE 
                        ? 'bg-green-100 text-green-800' 
                        : feature.status === FeatureStatus.COMPLETED
                        ? 'bg-amber-100 text-amber-900'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {feature.status}
                    </span>
                  </div>
                  
                  {feature.description && (
                    <p className="text-gray-600 text-sm mb-2 line-clamp-2">{feature.description}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    {feature.config?.type === 'decimal' && feature.unit && (
                      <div className="flex items-center gap-1">
                        <span>Unit:</span>
                        <span className="font-medium">{feature.unit}</span>
                      </div>
                    )}
                    
                    {feature.config?.type === 'enum' && (
                      <>
                        <div className="flex items-center gap-1">
                          <span>Type:</span>
                          <span className="font-medium">
                            Enum ({feature.config?.params?.type === 'num' ? 'Numbers' : 'Text'})
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>Values:</span>
                          <span className="font-medium">
                            {feature.config?.params?.values?.join(', ') || 'None'}
                          </span>
                        </div>
                      </>
                    )}
                    
                    {feature.config?.type === 'freeform' && (
                      <div className="flex items-center gap-1">
                        <span>Type:</span>
                        <span className="font-medium">Freeform Text</span>
                      </div>
                    )}
                    
                    {(!feature.config?.type || feature.config?.type === 'decimal') && (
                      <div className="flex items-center gap-1">
                        <span>Type:</span>
                        <span className="font-medium">Decimal Number</span>
                      </div>
                    )}
                    
                    {feature.config?.type === 'bool' && (
                      <div className="flex items-center gap-1">
                        <span>Type:</span>
                        <span className="font-medium">{boolLabels(feature.config?.params).join(' / ')}</span>
                      </div>
                    )}

                    {feature.config?.type === 'reference' && (
                      <div className="flex items-center gap-1">
                        <span>Type:</span>
                        <span className="font-medium">
                          Reference ({(() => {
                            const collectionId = feature.config?.params?.collection_id
                            const collection = collections.find(c => c.id === collectionId)
                            return collection ? collection.name : `Collection ID: ${collectionId || 'None'}`
                          })()})
                        </span>
                      </div>
                    )}
                    
                    {feature.config?.type === 'list' && (
                      <>
                        <div className="flex items-center gap-1">
                          <span>Type:</span>
                          <span className="font-medium">
                            List of {(() => {
                              const listType = feature.config?.params?.type
                              switch (listType) {
                                case 'str': return 'Texts'
                                case 'num': return 'Numbers'
                                case 'enum': return 'Enum Values'
                                case 'reference': return 'References'
                                case 'bool': return boolLabels(feature.config?.params).join(' / ')
                                default: return 'Items'
                              }
                            })()}
                          </span>
                        </div>
                        
                        {feature.config?.params?.type === 'enum' && feature.config?.params?.values && (
                          <div className="flex items-center gap-1">
                            <span>Allowed Values:</span>
                            <span className="font-medium">
                              {feature.config.params.values.join(', ')}
                            </span>
                          </div>
                        )}
                        
                        {feature.config?.params?.type === 'reference' && (
                          <div className="flex items-center gap-1">
                            <span>Collection:</span>
                            <span className="font-medium">
                              {(() => {
                                const collectionId = feature.config?.params?.collection_id
                                const collection = collections.find(c => c.id === collectionId)
                                return collection ? collection.name : `Collection ID: ${collectionId || 'None'}`
                              })()}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    
                    {feature.start_date && (
                      <div className="flex items-center gap-1">
                        <span>Start:</span>
                        <span className="font-medium">{formatDate(feature.start_date)}</span>
                      </div>
                    )}
                    
                    {feature.end_date && (
                      <div className="flex items-center gap-1">
                        <span>End:</span>
                        <span className="font-medium">{formatDate(feature.end_date)}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1">
                      <span>Created:</span>
                      <span className="font-medium">{formatDate(feature.created_at)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Right side - Action buttons */}
                <div className="flex flex-wrap gap-2 items-start">
                  <button
                    onClick={() => setEditMode(true)}
                    className="px-3 py-1 bg-action text-plum text-sm rounded hover:bg-action-dark"
                  >
                    Edit
                  </button>
                  
                  <div className="relative status-dropdown-container">
                    <button
                      onClick={() => setStatusMenuOpen(!statusMenuOpen)}
                      className="px-3 py-1 bg-secondary text-gray-700 text-sm rounded hover:bg-secondary-dark"
                    >
                      Actions
                    </button>
                    <div
                      className={`absolute left-0 sm:left-auto sm:right-0 z-10 mt-1 w-48 origin-top-left sm:origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none ${statusMenuOpen ? '' : 'hidden'}`}
                    >
                      {feature.status !== FeatureStatus.ACTIVE && (
                        <button
                          onClick={() => {
                            handleStatusChange(FeatureStatus.ACTIVE);
                            setStatusMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Activate Feature
                        </button>
                      )}

                      {feature.status !== FeatureStatus.COMPLETED && (
                        <button
                          onClick={() => {
                            handleStatusChange(FeatureStatus.COMPLETED);
                            setStatusMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Mark Completed
                        </button>
                      )}

                      {feature.status !== FeatureStatus.ARCHIVED && (
                        <button
                          onClick={() => {
                            handleStatusChange(FeatureStatus.ARCHIVED);
                            setStatusMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Archive Feature
                        </button>
                      )}

                      <div className="my-1 border-t border-gray-200"></div>
                      <button
                        onClick={() => {
                          setStatusMenuOpen(false);
                          setShowDeleteConfirm(true);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50"
                      >
                        Delete Feature
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* View Checkins Button Row */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Link
                  to={`/checkins?feature=${feature.id}`}
                  className="inline-flex items-center px-4 py-2 bg-action text-plum text-sm rounded-md hover:bg-action-dark transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  View Feature Checkins
                </Link>
              </div>
            </>
          )}
        </div>
        
        {/* Activity-summary cards for any config pinned to this feature page
            (Stage 6), with a "+ Add summary" header. Each card carries its own
            settings gear (Edit / Delete) — there's no separate config table.
            Rendered above the chart. */}
        {!editMode && feature && (() => {
          const pinned = summariesForFeature(summaryConfigs, feature.id)
          return (
            <div className="mb-4">
              <AddSummaryButton
                surface="feature"
                featureId={feature.id}
                features={[feature]}
                onChanged={reloadSummaryConfigs}
              />
              {pinned.length === 0 ? (
                <div className="py-2 text-sm text-gray-400">
                  No summaries here yet.
                </div>
              ) : (
                <div className="space-y-4">
                  {pinned.map((config) => (
                    <FeatureSummaryCard
                      key={config.id}
                      config={config}
                      featureName={feature.title}
                      unit={feature.unit}
                      surface="feature"
                      featureId={feature.id}
                      features={[feature]}
                      onChanged={reloadSummaryConfigs}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })()}

        {/* Chart driven by the per-user chart-type registry. The container
            picks the chart from the user's prefs (per-feature override →
            value-type default → cycle[0]). Hidden in edit mode. */}
        {!editMode && (
          <FeatureChartCard
            variant="card"
            featureId={parseInt(featureId || '0')}
            unit={feature.unit}
            config={feature.config}
            featureName={feature.title}
          />
        )}

      </div>
      
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteFeature}
        title="Delete Feature"
        message={`Are you sure you want to delete "${feature.title}"? This will remove all "${feature.title}" values derived from checkins.`}
        confirmText="Delete"
      />
    </div>
  )
}

export default FeatureDetailPage